import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  NavWrapper,
  NavContainer,
  Logo,
  NavLinks,
  NavLink,
  MobileMenuIcon,
  MobileMenu,
  MobileNavLink,
  DropdownMenu,
  DropdownItem,
  NavLinkWithDropdown,
  MobileDropdownItem,
  CloseIconWrapper,
  LogoContainer,
  PortfolioBar,
  PortfolioLinks,
  PortfolioLink,
  IconWrapper,
  MobileMenuHeader,
  LanguageSwitcherMobileContaner,
  MobileMenuContent,
  MobileDropdownMenuStyled,
} from './styles';

import digitalBusinessLogo from '@assets/images/blog/logo.svg';
import Icon from '@components/Icon';
import LanguageSwitcher from '@composites/LanguageSwitcher';
import {
  LANGUAGE,
  LANGUAGE_SWITCHER_MODE,
  LOCAL_STORAGE_TYPE,
} from '@utils/constants';
import { useMediaQuery } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { APP_HOST } from '@utils/apiHooks';
import { Blog } from '@hubs101/booking-api-skd-client/lib/types';

interface MenuItem {
  label: string;
  level: '0' | '1';
  order: number;
  type: 'internal' | 'external';
  url: string;
  buttonStyle?: boolean;
}

const DropdownMainItem = ({
  item,
  isActive,
}: {
  item: MenuItem;
  isActive: boolean;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <NavLink
      href={item.url}
      className={isActive ? 'active' : ''}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {item.label}
      <Icon
        name={isHovered ? 'caret_arrow_up' : 'caret_arrow'}
        size={14}
        color={
          isHovered || isActive
            ? theme.color.mobilityBlue
            : theme.color.mobilityGrey
        }
      />
    </NavLink>
  );
};

const BlogsNav = ({ blogs }: { blogs: Blog[] }) => {
  const isDesktop = useMediaQuery('(min-width: 951px)') ?? false;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  // Set default language to German on initial load (will be removed once English content is available)
  useEffect(() => {
    window.localStorage.setItem(LOCAL_STORAGE_TYPE.LANGUAGE, LANGUAGE.GERMAN);
    if (i18n.language !== LANGUAGE.GERMAN) {
      void i18n.changeLanguage(LANGUAGE.GERMAN);
    }
  }, [i18n]);

  const industryHubsUrl = t('industry-hubs-url');
  const slug = location.pathname.startsWith(`${industryHubsUrl}/`)
    ? location.pathname.replace(`${industryHubsUrl}/`, '').split('/')[0]
    : '';
  const b2bLeadServicesUrlTemplate = t(
    'mappable-company-routes.blog-content-driven-b2b-lead-data-solution-url'
  );
  const b2bLeadServicesUrl = slug
    ? b2bLeadServicesUrlTemplate.replace(':blogsSlug', slug)
    : industryHubsUrl;
  const currentBlog = useMemo(
    () => blogs?.find(blog => blog.slug === slug),
    [blogs, slug]
  );

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );
  const [desktopBlogsOpen, setDesktopBlogsOpen] = useState(true);

  const baseMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('portfolio-overview'),
        level: '0',
        order: 1,
        type: 'internal',
        url: t('mappable-company-routes.our-product-portfolio-url'),
      },
      {
        label: t('about-us'),
        level: '0',
        order: 2,
        type: 'internal',
        url: t('mappable-company-routes.about-us-url'),
      },
      {
        label: 'B2B Lead Services',
        level: '0',
        order: 3,
        type: 'internal',
        url: b2bLeadServicesUrl,
        buttonStyle: true,
      },
    ],
    [b2bLeadServicesUrl, t]
  );

  const blogSubItems: MenuItem[] = useMemo(() => {
    return (
      blogs?.map((blog, index) => ({
        label: blog.name,
        level: '1',
        order: index + 1, // temporary, will be recalculated
        type: 'internal',
        url: `${t('industry-hubs-url')}/${blog.slug}`,
      })) ?? []
    );
  }, [blogs, t]);

  const menuItems: MenuItem[] = useMemo(() => {
    const result: MenuItem[] = [];
    let currentOrder = 1;

    baseMenuItems.forEach(item => {
      result.push({ ...item, order: currentOrder++ });

      if (item.label === t('portfolio-overview')) {
        blogSubItems.forEach(subItem => {
          result.push({ ...subItem, order: currentOrder++ });
        });
      }
    });

    return result;
  }, [baseMenuItems, blogSubItems, t]);

  const isActive = (href: string) => location.pathname === href;

  const sortedItems = useMemo(
    () => [...menuItems].sort((a, b) => a.order - b.order),
    [menuItems]
  );

  const mainMenuItems = useMemo(
    () => sortedItems.filter(item => item.level === '0'),
    [sortedItems]
  );

  const secondaryMenuItems = useMemo(() => {
    return sortedItems
      .filter(item => item.level === '1')
      .map(item => ({
        ...item,
        parentIndex: mainMenuItems.findLastIndex(
          parent => parent.order < item.order
        ),
      }));
  }, [sortedItems, mainMenuItems]);

  const getSecondaryItems = (index: number) =>
    secondaryMenuItems.filter(item => item.parentIndex === index);

  const toggleMobileMenu = () => {
    setMobileOpen(prev => !prev);
    setOpenDropdownIndex(null);
  };

  const toggleDropdown = (index: number) => {
    setOpenDropdownIndex(prev => (prev === index ? null : index));
  };

  const toggleDesktopPortfolio = () => {
    setDesktopBlogsOpen(prev => !prev);
  };

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const scrollY = window.scrollY;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  const MobileDropdownMenu = ({
    isOpen,
    children,
  }: {
    isOpen: boolean;
    children: React.ReactNode;
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (ref.current) {
        setHeight(isOpen ? ref.current.scrollHeight : 0);
      }
    }, [isOpen, children]);

    return (
      <MobileDropdownMenuStyled ref={ref} height={height}>
        {children}
      </MobileDropdownMenuStyled>
    );
  };

  return (
    <NavWrapper>
      <NavContainer>
        <LogoContainer>
          <Logo
            src={digitalBusinessLogo}
            alt={currentBlog ? `${currentBlog.name} Logo` : 'WeConect Logo'}
            isMobile={!isDesktop}
            onClick={() => {
              window.location.href = `${APP_HOST}${t('industry-hubs-url')}`;
            }}
          />
        </LogoContainer>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NavLinks>
            {mainMenuItems.map((item, index) => {
              const subItems = getSecondaryItems(index);
              const hasSubItems = subItems.length > 0;

              // Desktop special handling for portfolio-overview
              if (isDesktop && item.label === t('portfolio-overview')) {
                const isActiveItem = desktopBlogsOpen || isActive(item.url);
                return (
                  <NavLink
                    key={item.label}
                    onClick={toggleDesktopPortfolio}
                    className={isActiveItem ? 'active' : ''}
                  >
                    {item.label}
                    <Icon
                      name={desktopBlogsOpen ? 'caret_arrow_up' : 'caret_arrow'}
                      size={18}
                      color={
                        desktopBlogsOpen
                          ? theme.color.mobilityBlue
                          : theme.color.mobilityGrey
                      }
                    />
                  </NavLink>
                );
              }

              if (hasSubItems) {
                return (
                  <NavLinkWithDropdown key={item.label}>
                    <DropdownMainItem
                      item={item}
                      isActive={isActive(item.url)}
                    />

                    <DropdownMenu>
                      {subItems.map(sub => (
                        <DropdownItem
                          key={sub.label}
                          href={sub.url}
                          className={isActive(sub.url) ? 'active' : ''}
                        >
                          {sub.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </NavLinkWithDropdown>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  href={item.url}
                  className={isActive(item.url) ? 'active' : ''}
                  buttonStyle={item.buttonStyle}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </NavLinks>

          {isDesktop && (
            <>
              <LanguageSwitcher
                mode={LANGUAGE_SWITCHER_MODE.LIGHT}
                fontWeight={theme.fontWeight.weight600}
                hideEN={true}
              />
              {false && (
                <IconWrapper>
                  <Icon
                    name="search"
                    size={18}
                    color={theme.color.mobilityGrey}
                  />
                </IconWrapper>
              )}
            </>
          )}
        </div>

        <MobileMenuIcon onClick={toggleMobileMenu}>☰</MobileMenuIcon>

        {mobileOpen && (
          <MobileMenu>
            <MobileMenuHeader>
              <LogoContainer>
                <Logo
                  src={digitalBusinessLogo}
                  alt={
                    currentBlog ? `${currentBlog.name} Logo` : 'WeConect Logo'
                  }
                  isMobile={!isDesktop}
                  onClick={() => {
                    window.location.href = `${APP_HOST}${t('industry-hubs-url')}`;
                  }}
                />
              </LogoContainer>

              <CloseIconWrapper onClick={toggleMobileMenu}>
                <Icon
                  size={40}
                  name="close"
                  color={theme.color.mobilityGrey}
                  indentLess
                />
              </CloseIconWrapper>
            </MobileMenuHeader>

            <MobileMenuContent>
              {mainMenuItems.map((item, index) => {
                const subItems = getSecondaryItems(index);
                const hasSubItems = subItems.length > 0;

                return (
                  <React.Fragment key={item.label}>
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <MobileNavLink
                        href={hasSubItems ? '#' : item.url}
                        className={isActive(item.url) ? 'active' : ''}
                        buttonStyle={item.buttonStyle}
                        onClick={e => {
                          if (hasSubItems) {
                            e.preventDefault();
                            toggleDropdown(index);
                          }
                        }}
                      >
                        {item.label}
                        {hasSubItems && (
                          <Icon
                            size={18}
                            color={theme.color.mobilityGrey}
                            name={
                              openDropdownIndex === index
                                ? 'caret_arrow_up'
                                : 'caret_arrow'
                            }
                            indentLess
                          />
                        )}
                      </MobileNavLink>
                    </div>

                    {hasSubItems && (
                      <MobileDropdownMenu isOpen={openDropdownIndex === index}>
                        {subItems.map(sub => (
                          <MobileDropdownItem
                            key={sub.label}
                            href={sub.url}
                            className={isActive(sub.url) ? 'active' : ''}
                          >
                            {sub.label}
                          </MobileDropdownItem>
                        ))}
                      </MobileDropdownMenu>
                    )}
                  </React.Fragment>
                );
              })}

              <LanguageSwitcherMobileContaner>
                <LanguageSwitcher
                  mode={LANGUAGE_SWITCHER_MODE.LIGHT}
                  hideEN={true}
                />
              </LanguageSwitcherMobileContaner>
            </MobileMenuContent>
          </MobileMenu>
        )}
      </NavContainer>

      {isDesktop && (blogs?.length ?? 0) > 0 && (
        <PortfolioBar id="portfolio-bar" isOpen={desktopBlogsOpen}>
          <PortfolioLinks>
            {blogs?.map(blog => (
              <PortfolioLink
                key={`portfolio-link-${blog.slug}`}
                isActive={slug === blog.slug}
                onClick={() => {
                  window.location.href = `${APP_HOST}${t(
                    'industry-hubs-url'
                  )}/${blog.slug}`;
                }}
              >
                {blog.name}
              </PortfolioLink>
            ))}
          </PortfolioLinks>
        </PortfolioBar>
      )}
    </NavWrapper>
  );
};

export default BlogsNav;
